import { z } from "zod";
import { consultantKpiObjectivesSchema } from "./consultant-kpi-objectives";
import { dateFilter } from "../list";
import { zodDateSchema } from "../shared";

export const teamMemberDataTypeSchema = z.enum(["consultant_kpi"]);
export type TeamMemberDataType = z.infer<typeof teamMemberDataTypeSchema>;

/** Opaque JSON stored in `team_member_data.metadata`. Shape is owned by each type builder. */
export const teamMemberDataMetadataSchema = z.record(z.string(), z.unknown());
export type TeamMemberDataMetadata = z.infer<typeof teamMemberDataMetadataSchema>;

/** Week is Monday-to-date, month is 1st-to-date. */
export const consultantKpiPeriodSchema = z.enum(["week", "month"]);
export type ConsultantKpiPeriod = z.infer<typeof consultantKpiPeriodSchema>;

export const consultantKpiCallTrendPointSchema = z.object({
  startDate: z.string(),
  talentCallCount: z.number(),
});
export type ConsultantKpiCallTrendPoint = z.infer<typeof consultantKpiCallTrendPointSchema>;

export const consultantKpiCallsSchema = z.object({
  talentCallCount: z.number(),
  trend: z.array(consultantKpiCallTrendPointSchema),
});
export type ConsultantKpiCalls = z.infer<typeof consultantKpiCallsSchema>;

export const consultantKpiPlanningSchema = z.object({
  temporaryWorkerCount: z.number(),
  fullTimeEquivalent: z.number(),
  averageCoefficient: z.number().nullable(),
  averageHourlyWage: z.number().nullable(),
});
export type ConsultantKpiPlanning = z.infer<typeof consultantKpiPlanningSchema>;

export const consultantKpiMissionsSchema = z.object({
  newCompanyCount: z.number(),
  existingCompanyCount: z.number(),
});
export type ConsultantKpiMissions = z.infer<typeof consultantKpiMissionsSchema>;

export const consultantKpiContractsSchema = z.object({
  createdCount: z.number(),
  withFirstContractTalentCount: z.number(),
  withTalentPreviousEndAtLeast30DaysCount: z.number(),
  withTalentPreviousEndAtLeast120DaysCount: z.number(),
  newTalentPlacementCount: z.number(),
  existingTalentPlacementCount: z.number(),
  planningRetentionPercent: z.number().nullable(),
});
export type ConsultantKpiContracts = z.infer<typeof consultantKpiContractsSchema>;

export const consultantKpiPldStatusSchema = z.enum(["success", "error"]);
export type ConsultantKpiPldStatus = z.infer<typeof consultantKpiPldStatusSchema>;

export const consultantKpiPldErrorKeys = {
  notConfigured: "consultant_kpi.pld.not_configured",
  consultantNameMissing: "consultant_kpi.pld.consultant_name_missing",
  notFound: "consultant_kpi.pld.not_found",
  queryFailed: "consultant_kpi.pld.query_failed",
} as const;

export type ConsultantKpiPldErrorKey = (typeof consultantKpiPldErrorKeys)[keyof typeof consultantKpiPldErrorKeys];

export const consultantKpiPldErrorKeySchema = z.enum([
  consultantKpiPldErrorKeys.notConfigured,
  consultantKpiPldErrorKeys.consultantNameMissing,
  consultantKpiPldErrorKeys.notFound,
  consultantKpiPldErrorKeys.queryFailed,
]);

export const buildConsultantNameCandidates = (params: { firstName: string; lastName: string }): [string, string] => [
  [params.firstName, params.lastName].filter(Boolean).join(" "),
  [params.lastName, params.firstName].filter(Boolean).join(" "),
];

export const consultantKpiPldTrendPointSchema = z.object({
  /** `YYYYMM` for a month curve, `YYYY-MM-DD` for a week curve (one point per day). */
  period: z.string().regex(/^\d{6}$|^\d{4}-\d{2}-\d{2}$/),
  netMargin: z.number(),
});
export type ConsultantKpiPldTrendPoint = z.infer<typeof consultantKpiPldTrendPointSchema>;

export const consultantKpiPldSchema = z.object({
  netMargin: z.number().nullable(),
  netMarginRate: z.number().nullable(),
  revenue: z.number().nullable(),
  period: z
    .string()
    .regex(/^\d{6}$/)
    .nullable(),
  netMarginTrend: z.array(consultantKpiPldTrendPointSchema),
  status: consultantKpiPldStatusSchema,
  errorKey: consultantKpiPldErrorKeySchema.nullable(),
});
export type ConsultantKpiPld = z.infer<typeof consultantKpiPldSchema>;

/** One consultant KPI fact for a calendar day (stored in `team_member_data.metadata`; day comes from `created_at`). */
export const consultantKpiDailySnapshotCallsSchema = z.object({
  talentCallCount: z.number(),
});
export type ConsultantKpiDailySnapshotCalls = z.infer<typeof consultantKpiDailySnapshotCallsSchema>;

export const consultantKpiDailySnapshotSchema = z.object({
  calls: consultantKpiDailySnapshotCallsSchema,
  planning: consultantKpiPlanningSchema,
  missions: consultantKpiMissionsSchema,
  contracts: consultantKpiContractsSchema,
  pld: consultantKpiPldSchema,
});
export type ConsultantKpiDailySnapshot = z.infer<typeof consultantKpiDailySnapshotSchema>;

export const parseConsultantKpiDailySnapshot = (
  metadata: Record<string, unknown>
): ConsultantKpiDailySnapshot | null => {
  const parsed = consultantKpiDailySnapshotSchema.safeParse(metadata);
  return parsed.success ? parsed.data : null;
};

/** Consultant KPI metrics for a period, assembled from daily snapshots (no objectives). */
export const consultantKpiMetricsSchema = z.object({
  period: consultantKpiPeriodSchema,
  periodStartDate: z.string(),
  periodEndDate: z.string(),
  calls: consultantKpiCallsSchema,
  planning: consultantKpiPlanningSchema,
  missions: consultantKpiMissionsSchema,
  contracts: consultantKpiContractsSchema,
  pld: consultantKpiPldSchema,
});
export type ConsultantKpiMetrics = z.infer<typeof consultantKpiMetricsSchema>;

/** Consultant KPI for a period, including business-unit objectives resolved by the API. */
export const consultantKpiSchema = consultantKpiMetricsSchema.extend({
  objectives: consultantKpiObjectivesSchema,
  /** Same period one week/month earlier, truncated at the same elapsed point. Null when no snapshot exists. */
  previousPeriod: consultantKpiMetricsSchema.nullable(),
});
export type ConsultantKpi = z.infer<typeof consultantKpiSchema>;

/** Row stored in `business_unit_objective`. */
export const businessUnitObjectiveRowSchema = z.object({
  business_unit_id: z.guid(),
  period: consultantKpiPeriodSchema,
  objectives: consultantKpiObjectivesSchema,
  created_at: z.string(),
  updated_at: z.string(),
});
export type BusinessUnitObjectiveRow = z.infer<typeof businessUnitObjectiveRowSchema>;

export const getConsultantKpiQuerySchema = z.object({
  teamMemberId: z.guid(),
  period: consultantKpiPeriodSchema.default("week"),
  referenceDate: dateFilter,
});
export type GetConsultantKpiQuery = z.infer<typeof getConsultantKpiQuerySchema>;

export const teamMemberDataSchema = z.object({
  id: z.guid(),
  team_member_id: z.guid(),
  type: teamMemberDataTypeSchema,
  metadata: teamMemberDataMetadataSchema,
  created_at: zodDateSchema,
});
export type TeamMemberData = z.infer<typeof teamMemberDataSchema>;

export const listTeamMemberDataQuerySchema = z.object({
  teamMemberIds: z.array(z.guid()).min(1),
  type: teamMemberDataTypeSchema.default("consultant_kpi"),
  createdAtFrom: dateFilter,
  createdAtTo: dateFilter,
});
export type ListTeamMemberDataQuery = z.infer<typeof listTeamMemberDataQuerySchema>;

export const listTeamMemberDataResponseSchema = z.object({
  items: z.array(teamMemberDataSchema),
});
export type ListTeamMemberDataResponse = z.infer<typeof listTeamMemberDataResponseSchema>;

export const createTeamMemberDataBodySchema = z.object({
  teamMemberIds: z.array(z.guid()).min(1),
  type: teamMemberDataTypeSchema,
});
export type CreateTeamMemberDataBody = z.infer<typeof createTeamMemberDataBodySchema>;

/** Row shape handed to the repository on insert (team members resolved from the request body). */
export const createTeamMemberData = z.object({
  team_member_id: z.guid(),
  type: teamMemberDataTypeSchema,
  metadata: teamMemberDataMetadataSchema,
});
export type CreateTeamMemberData = z.infer<typeof createTeamMemberData>;

export const teamMemberDataListParamsSchema = z.object({
  teamMemberIds: z.array(z.guid()).min(1),
  type: teamMemberDataTypeSchema.optional(),
  createdAtFrom: dateFilter,
  createdAtTo: dateFilter,
});
export type TeamMemberDataListParams = z.infer<typeof teamMemberDataListParamsSchema>;
